import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Btn, SectionHeader, StatCard, Badge, ProgressBar, Modal, Input, Select, Textarea, FormGrid, RNLLoader, HeaderBack } from '../components/UI';
import { fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';

export default function ProjectDivisionDetailsPage({ toast }) {
  const { projectId, projectDivisionId } = useParams();
  const navigate = useNavigate();
  const [projectDivision, setProjectDivision] = useState(null);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadProjectDivision = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getProjectDivision(projectDivisionId);
      setProjectDivision(data);
    } catch (err) {
      toast.show(err.message || 'Failed to load projectDivision', 'error');
    }
    setLoading(false);
  }, [projectDivisionId, toast]);

  useEffect(() => {
    if (projectDivisionId) loadProjectDivision();
  }, [projectDivisionId, loadProjectDivision]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const saveMilestone = async () => {
    setSaving(true);
    try {
      if (form.milestone_id) await api.updateMilestone(form.milestone_id, form);
      else                   await api.createProjectDivisionMilestone(projectDivisionId, form);
      toast.show('Milestone saved', 'success'); 
      setModal(false); 
      loadProjectDivision();
    } catch (e) { 
      toast.show(e.message, 'error'); 
    }
    setSaving(false);
  };

  const delMilestone = async (id) => {
    if (!window.confirm('Delete milestone?')) return;
    try { 
      await api.deleteMilestone(id); 
      toast.show('Deleted', 'success'); 
      loadProjectDivision(); 
    } catch (e) { 
      toast.show(e.message, 'error'); 
    }
  };

  if (loading && !projectDivision) {
    return <RNLLoader title="Loading Project Division..." subtitle="Please wait" />;
  }

  if (!projectDivision) {
    return (
      <div className="fade-in">
        <HeaderBack onClick={() => navigate(`/projects/${projectId}/projectDivisions`)} label="Back to Project Divisions" />
        <SectionHeader title="Project Division Details" subtitle="ProjectDivision not found" />
      </div>
    );
  }

  const completedMilestones = projectDivision.milestones?.filter(m => m.is_completed).length || 0;
  const totalMilestones = projectDivision.milestones?.length || 0;

  return (
    <div className="fade-in">
      <HeaderBack onClick={() => navigate(`/projects/${projectId}/projectDivisions`)} label="Back to Project Divisions" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{projectDivision.name}</h2>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>
            Code: <strong style={{ color: 'var(--accent)' }}>{projectDivision.code}</strong> · Package: {projectDivision.work_package || '—'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Badge color={statusColor(projectDivision.status)}>{projectDivision.status}</Badge>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Floor Count" value={projectDivision.floor_count} icon="🏢" color="cyan" />
        <StatCard label="Gross Floor Area" value={`${fmtNum(projectDivision.gross_floor_area)} m²`} icon="📐" color="accent" />
        <StatCard label="Floor Level" value={projectDivision.floor_level || '—'} icon="🪜" color="indigo" />
        <StatCard label="Area/Room" value={projectDivision.area_room || '—'} icon="🚪" color="purple" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Planned Start" value={fmtDate(projectDivision.planned_start) || '—'} icon="📅" />
        <StatCard label="Planned Finish" value={fmtDate(projectDivision.planned_finish) || '—'} icon="🏁" />
        <StatCard label="Actual Start" value={fmtDate(projectDivision.actual_start) || '—'} icon="▶️" color="green" />
        <StatCard label="Actual Finish" value={fmtDate(projectDivision.actual_finish) || '—'} icon="🛑" color="blue" />
      </div>

      <Card style={{ marginBottom: 24, padding: '20px 24px' }}>
        <div className="grid-cols-2" style={{ gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Description</div>
            <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-primary)' }}>{projectDivision.description || 'No description provided.'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Discipline</div>
            <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-primary)' }}>{projectDivision.discipline?.discipline_name || '—'}</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Associated Milestones</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{completedMilestones}/{totalMilestones} completed</span>
          <Btn variant="ghost" onClick={() => exportCSV(projectDivision.milestones || [], 'project_division_milestones')}>⬇ CSV</Btn>
          <Btn style={{ background: '#10b981', color: '#fff', border: 'none' }} onClick={() => { setForm({ completion_percentage: '0', is_completed: false }); setModal(true); }}>
            + Add Milestone
          </Btn>
        </div>
      </div>

      <Card>
        <Table loading={loading} cols={[
          { key: 'name',                 label: 'Milestone',   render: v => <strong>{v}</strong> },
          { key: 'planned_date',         label: 'Planned',     render: v => fmtDate(v) },
          { key: 'actual_date',          label: 'Actual',      render: v => fmtDate(v) },
          { key: 'completion_percentage',label: 'Progress',    render: v => <ProgressBar value={parseFloat(v || 0)} max={100} /> },
          { key: 'is_completed',         label: 'Status',      render: v => <Badge color={v ? 'green' : 'amber'}>{v ? '✓ Done' : 'Pending'}</Badge> },
          { key: 'description',          label: 'Notes',       render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
          { key: 'milestone_id',         label: '',            render: (id, row) => (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn variant="ghost"  size="sm" style={{ padding: '3px 8px' }} onClick={() => { setForm({ ...row }); setModal(true); }}>Edit</Btn>
              <Btn variant="danger" size="sm" style={{ padding: '3px 8px' }} onClick={() => delMilestone(id)}>Del</Btn>
            </div>
          )},
        ]} rows={projectDivision.milestones || []} />
      </Card>

      {modal && (
        <Modal title={form.milestone_id ? 'Edit Milestone' : 'New Milestone'} onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Name" required value={form.name || ''} onChange={set('name')} />
            <FormGrid>
              <Input label="Planned Date" type="date" value={form.planned_date || ''} onChange={set('planned_date')} />
              <Input label="Actual Date"  type="date" value={form.actual_date  || ''} onChange={set('actual_date')} />
              <Input label="Completion %" type="number" value={form.completion_percentage || '0'} onChange={set('completion_percentage')} />
              <Select label="Completed?" value={form.is_completed ? 'yes' : 'no'} onChange={v => setForm(f => ({ ...f, is_completed: v === 'yes' }))}
                options={[{ value: 'no', label: 'No — Pending' }, { value: 'yes', label: 'Yes — Complete' }]} />
            </FormGrid>
            <Textarea label="Description" value={form.description || ''} onChange={set('description')} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={saveMilestone} loading={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
